import * as React from 'react';
import { Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import AdminTabsContext, { AdminTabsContextValues } from 'components/shared/edit/tabs/AdminTabsContext';
import DefaultSuggestedValue from './DefaultSuggestedValue';

type stringFunction = (inputValue: string | unknown) => string | unknown;

interface WhenFieldChangesProps {
	field: string;
	becomes?: any;
	set: string;
	to: string | stringFunction;
	requireUserConfirmation?: boolean;
	renderSuggestedValue?: (suggestedValue: string, applySuggestedValue: () => void) => React.ReactNode;
}

interface WhenFieldChangesState {
	suggestedValue: string;
}

/**
 * Component that allows you to set the value of a field based on the value of another.
 *
 * @class WhenFieldChanges
 * @extends {React.Component<WhenFieldChangesProps, WhenFieldChangesState>}
 */
class WhenFieldChanges extends React.Component<WhenFieldChangesProps, WhenFieldChangesState> {
	public static contextType: React.Context<any> = AdminTabsContext;
	public declare context: AdminTabsContextValues;

	constructor(props) {
		super(props);

		this.state = {
			suggestedValue: '',
		};
	}

	public render() {
		const { field, becomes, set, to, requireUserConfirmation, renderSuggestedValue } = this.props;

		return (
			// in react-final-form, <Field /> is really just a subscription to a specific field, based on `name`
			// this is pretty much the same as a <FormSpy /> but for a specific field only.
			<Field name={set} subscription={{ value: true }}>
				{(props) => {
					const {
						input: { onChange, value },
					} = props;

					// function that will update the value of the form field in react-final-form.
					const applySuggestedValue = () => {
						onChange(this.state.suggestedValue);

						// resets the suggest value.
						this.setState({
							suggestedValue: '',
						});
					};

					return (
						<>
							{/* only display the suggestion if the prop is set, and the current value of the field
								is different than the suggestion. this uses the render props pattern to allow you to modify
								how this looks/works from other components that could use this. */}

							{requireUserConfirmation &&
							this.state.suggestedValue &&
							this.state.suggestedValue !== value ? (
								renderSuggestedValue ? (
									renderSuggestedValue(this.state.suggestedValue, applySuggestedValue)
								) : (
									<DefaultSuggestedValue
										suggestedValue={this.state.suggestedValue}
										applySuggestedValue={applySuggestedValue}
									/>
								)
							) : null}

							<OnChange name={field}>
								{(fieldValue) => {
									if (becomes !== undefined && fieldValue !== becomes) {
										return;
									}

									let newValue;

									// we allow users to pass a string if it's always constant, or
									// a function to do custom operations
									if (typeof to === 'function') {
										newValue = to(fieldValue);
									} else {
										newValue = fieldValue;
									}

									// only update the value on the form as long as we're not requiring
									// user confirmation
									if (!requireUserConfirmation) {
										onChange(newValue);
									} else {
										// store the suggestion in state.
										this.setState({
											suggestedValue: newValue,
										});
										this.context.highlightTab(this.context.parentTab);
									}
								}}
							</OnChange>
						</>
					);
				}}
			</Field>
		);
	}
}

export default WhenFieldChanges;
